import React, { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Bell, ChevronDown, Lock, LogOut, Menu, Search, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useSchool } from '@/lib/useSchool';
import { useAuth } from '@/stores/auth';
import { api } from '@/lib/api';

export interface GroupChild { to: string; label: string; badge?: number; badgeTone?: 'green' | 'orange' | 'red'; also?: string[]; /** switch-off key (defaults to `to`) */ gate?: string }
export interface Group { key: string; section: string; icon: React.ComponentType<{ className?: string }>; items: GroupChild[] }

/**
 * The school-staff shell (owner, desk officer): navy sidebar whose sections are dropdowns — the section of the
 * current page opens by itself — plus a top bar with a quick search and notifications.
 */
export function GroupedLayout({ base, roleLabel, groups: allGroups, searchTo, badges, padded = false, ownPadding = [], hidden = [] }: {
  base: string; roleLabel: string; groups: Group[];
  /** Pages switched off by the owner: not in the menu, and a notice instead of the page. */
  hidden?: string[];
  /** Pad the page area (for pages that don't pad themselves), except the pages listed in ownPadding. */
  padded?: boolean; ownPadding?: string[];
  /** Where the top search goes, e.g. 'students' → `${base}/students?q=…`. No search box when omitted. */
  searchTo?: string;
  /** Live counts by item `to`. */
  badges?: Record<string, number | undefined>;
}) {
  const off = (i: GroupChild) => hidden.includes(i.gate ?? i.to);
  const groups = allGroups.map((g) => ({ ...g, items: g.items.filter((i) => !off(i)) })).filter((g) => g.items.length);
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const brand = useSchool();
  const [logoBroken, setLogoBroken] = useState(false);
  const schoolName = brand.name || user?.schoolName || 'School';

  const match = location.pathname.match(new RegExp(`^${base}/([^/]+)`));
  const activePage = match?.[1] ?? 'dashboard';
  const groupOf = (page: string) => groups.find((g) => g.items.some((i) => i.to === page || i.also?.includes(page)))?.key ?? null;
  const activeGroup = groupOf(activePage);
  const [expanded, setExpanded] = useState<string | null>(activeGroup ?? groups[0]?.key ?? null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [q, setQ] = useState('');

  useEffect(() => { if (activeGroup) setExpanded(activeGroup); setMobileOpen(false); }, [activeGroup, location.pathname]);
  useEffect(() => { document.body.style.overflow = mobileOpen ? 'hidden' : ''; return () => { document.body.style.overflow = ''; }; }, [mobileOpen]);

  const unread = useQuery({ queryKey: ['nav-unread'], queryFn: () => api.get('/notifications', { params: { unread: 1, per_page: 1 } }).then((r) => Number(r.data?.meta?.total ?? 0)), refetchInterval: 60_000, retry: false });

  const signOut = () => { logout(); navigate('/login', { replace: true }); };
  const initials = (user?.name ?? roleLabel).split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();
  const current = groups.flatMap((g) => g.items).find((i) => i.to === activePage || i.also?.includes(activePage));
  const blocked = allGroups.flatMap((g) => g.items).find((i) => (i.to === activePage || i.also?.includes(activePage)) && off(i));
  const search = (e: React.FormEvent) => { e.preventDefault(); if (searchTo && q.trim()) navigate(`${base}/${searchTo}?q=${encodeURIComponent(q.trim())}`); };

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className={`drawer-safe fixed md:sticky md:top-0 inset-y-0 left-0 z-40 w-72 max-w-[85vw] md:w-64 md:h-screen bg-[#0A2540] text-slate-100 flex flex-col transform transition-transform duration-200 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}>
        <div className="px-5 py-5 border-b border-white/10 flex items-center gap-3">
          {brand.logo && !logoBroken
            ? <img src={brand.logo} alt="" onError={() => setLogoBroken(true)} className="w-9 h-9 rounded-lg object-contain bg-white shrink-0" />
            : <div className="w-9 h-9 rounded-lg bg-green-600 flex items-center justify-center text-white font-extrabold shrink-0">{schoolName.charAt(0).toUpperCase()}</div>}
          <div className="flex-1 min-w-0"><div className="text-sm font-bold text-white truncate">{schoolName}</div><div className="text-[11px] text-slate-400 truncate">{roleLabel}</div></div>
          <button className="md:hidden tap-target text-slate-300" onClick={() => setMobileOpen(false)} aria-label="Close menu"><X className="w-6 h-6" /></button>
        </div>

        <nav className="flex-1 overflow-y-auto py-3">
          {groups.map((g) => {
            const isOpen = expanded === g.key;
            const isActive = activeGroup === g.key;
            const Icon = g.icon;
            const total = g.items.reduce((n, i) => n + (badges?.[i.to] ?? i.badge ?? 0), 0);
            return (
              <div key={g.key} className="mb-1">
                <button onClick={() => setExpanded(isOpen ? null : g.key)}
                  className={`w-full flex items-center justify-between px-4 py-2.5 text-left text-sm font-semibold transition border-l-2 ${isActive ? 'text-white bg-white/5 border-green-500' : 'text-slate-300 hover:text-white hover:bg-white/5 border-transparent'}`}>
                  <span className="flex items-center gap-2.5"><Icon className="w-4 h-4 shrink-0" />{g.section}</span>
                  <span className="flex items-center gap-2">
                    {!isOpen && total > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-green-500 text-white">{total}</span>}
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </span>
                </button>
                {isOpen && (
                  <ul className="py-1">
                    {g.items.map((item) => {
                      const active = item.to === activePage || !!item.also?.includes(activePage);
                      const n = badges?.[item.to] ?? item.badge;
                      return (
                        <li key={item.to}>
                          <Link to={`${base}/${item.to}`}
                            className={`flex items-center justify-between pr-4 pl-11 py-2 text-[13px] transition border-l-2 ${active ? 'text-white bg-green-600/20 font-semibold border-green-500' : 'text-slate-400 hover:text-white hover:bg-white/5 border-transparent'}`}>
                            <span>{item.label}</span>
                            {!!n && n > 0 && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${item.badgeTone === 'orange' ? 'bg-amber-500' : item.badgeTone === 'red' ? 'bg-red-500' : 'bg-green-500'} text-white`}>{n}</span>}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </nav>

        <div className="border-t border-white/10 p-3 flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center text-xs font-bold text-white shrink-0">{initials}</div>
          <div className="flex-1 min-w-0"><div className="text-xs font-semibold text-white truncate">{user?.name}</div><div className="text-[10px] text-slate-400 truncate">{roleLabel}</div></div>
          <button onClick={signOut} className="text-slate-400 hover:text-white p-1" title="Sign out"><LogOut className="w-4 h-4" /></button>
        </div>
      </aside>

      {mobileOpen && <div className="fixed inset-0 bg-black/50 z-30 md:hidden" onClick={() => setMobileOpen(false)} />}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="mobile-topbar md:min-h-0 sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-slate-200 px-2 md:px-6 md:py-2.5 flex items-center gap-1 md:gap-3">
          <button onClick={() => setMobileOpen(true)} className="md:hidden tap-target text-slate-700" aria-label="Open menu"><Menu className="w-6 h-6" /></button>
          <span className="md:hidden font-bold text-slate-900 truncate flex-1">{current?.label ?? schoolName}</span>
          {searchTo && (
            <form onSubmit={search} className="hidden md:flex flex-1 max-w-md relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search students, reg no. or parent phone" className="w-full pl-9 pr-3 py-2 rounded-xl bg-slate-100 border border-transparent focus:bg-white focus:border-slate-300 text-sm outline-none" />
            </form>
          )}
          <div className="ml-auto flex items-center gap-3">
            <Link to={`${base}/notifications`} className="relative p-1.5 text-slate-600 hover:text-slate-900" aria-label="Notifications">
              <Bell className="w-5 h-5" />
              {!!unread.data && <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold grid place-items-center">{unread.data > 9 ? '9+' : unread.data}</span>}
            </Link>
            <div className="hidden sm:flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-[#0A2540] text-white text-xs font-bold grid place-items-center">{initials}</span>
              <span className="leading-tight"><span className="block text-sm font-semibold text-slate-900">{user?.name}</span><span className="block text-[11px] text-slate-500">{roleLabel}</span></span>
            </div>
          </div>
        </header>
        <main className={`flex-1 min-w-0 ${padded && !ownPadding.includes(activePage) ? 'p-4 sm:p-6' : ''}`}>
          {blocked ? (
            <div className="max-w-md mx-auto my-16 bg-white rounded-2xl border border-slate-200 p-6 text-center">
              <Lock className="w-8 h-8 text-slate-300 mx-auto" />
              <h1 className="font-bold text-slate-900 mt-2">{blocked.label} is turned off</h1>
              <p className="text-sm text-slate-500 mt-1">The school owner has switched this page off for your role. Ask them if you need it.</p>
              <Link to={`${base}/dashboard`} className="inline-block mt-4 px-4 py-2 rounded-xl bg-[#0A2540] text-white text-sm font-semibold">Back to dashboard</Link>
            </div>
          ) : <Outlet />}
        </main>
      </div>
    </div>
  );
}
